import { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Input,
	Modal,
	Select,
	Space,
	Spin,
	Table,
	Tag,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { v4 as uuid } from 'uuid';
import { getApi, postApi } from 'redux/apis';
import { toastText } from 'utils/utils';
import {
	computeEmployeeRows,
	type RosterEmployee,
} from '../SmartImport/steps/step3FuzzyMatch';
import './index.scss';

const EMPLOYEES_URL = '/employees/list';
const ALIASES_URL = '/employee-cost/import/name-aliases';
const UPDATE_STATUS_URL = '/employees/update-status';
const CREATE_EMPLOYEE_URL = '/employees/create';

// Sentinel for the pinned "+ Add as new employee" override option (mirrors the
// Step 2 / Step 3 CREATE_NEW pattern). Never sent to the server.
const ADD_AS_NEW_VALUE = '__add_as_new__';

// Local row shape — the wizard's employeeRows element plus this panel's own
// manual `reactivate` toggle (the Smart Import wizard auto-detects reactivations
// per ENG-370, so the shared row type no longer carries that flag). The panel
// reuses the Step 3 matching utilities, NOT the Step 3 component or its reducer
// (prep §2, Strategy B). State is local. `addAsNewName` set (non-null) marks a
// row the user wants created.
type MismatchRow = ReturnType<typeof computeEmployeeRows>[number] & {
	reactivate: boolean;
};

interface QboTimeMismatchPanelProps {
	open: boolean;
	companyId: string;
	payPeriodId: string | null; // current pay period — seeds the new employee's $0 cost rows
	unmatchedNames: string[]; // the QBO names to resolve
	onClose: () => void;
	onSaved: () => void; // called after a successful save; host re-fetches unmatched names
}

/**
 * WS-13 (ENG-332) — QBO Time mismatch resolution panel (Functional Spec §10.2),
 * extended by ENG-352 with the add-as-new path.
 *
 * Opened from the mismatch banner. For each unmatched QBO DisplayName it computes a
 * fuzzy match against the company roster (reusing `computeEmployeeRows`) and lets the
 * user (a) remap to an existing employee, (b) reactivate an inactive match, or (c)
 * add the name as a brand-new employee. On save it creates new employees (POST
 * /employees/create), reactivates flagged employees, then upserts `qbo_time_name`
 * aliases so the names auto-resolve on the next sync.
 */
export default function QboTimeMismatchPanel({
	open,
	companyId,
	payPeriodId,
	unmatchedNames,
	onClose,
	onSaved,
}: QboTimeMismatchPanelProps) {
	const [roster, setRoster] = useState<RosterEmployee[]>([]);
	const [rows, setRows] = useState<MismatchRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	// rowId → in-progress add-as-new name while its inline input is open. A row is
	// "being named" iff its id is a key here; confirming moves the value to the
	// row's addAsNewName and removes the key.
	const [pendingAddNew, setPendingAddNew] = useState<Record<string, string>>({});

	// Load roster + saved qbo_time_name aliases and compute initial matches whenever
	// the panel opens. `destroyOnHidden` on the Modal resets this state between opens.
	useEffect(() => {
		if (!open) return;
		if (!companyId) {
			setLoadError('Missing company context.');
			return;
		}
		let cancelled = false;
		const load = async () => {
			setLoading(true);
			setLoadError(null);
			try {
				const [rosterRes, aliasRes] = await Promise.all([
					postApi(
						EMPLOYEES_URL,
						{ companyId, status: 'all' },
						false,
						{ limit: 10000, page: 1 }
					),
					getApi(ALIASES_URL, { companyId, mappingType: 'qbo_time_name' }),
				]);
				if (cancelled) return;

				// DefaultResponse wrapper + service result both nest under `data`:
				// response.data.data.data is the employee array.
				const rosterResult: RosterEmployee[] =
					rosterRes.data?.data?.data ?? [];
				setRoster(rosterResult);

				const aliasMap = new Map<string, string>();
				for (const a of aliasRes.data?.aliases ?? []) {
					if (a.csvValue && a.employeeId) {
						aliasMap.set(a.csvValue, a.employeeId);
					}
				}

				// WS-13 has no file matrix — synthesize one aggregated row per name.
				// confidence is always null here: this panel has no AI extraction step
				// (see PdfExtractionMetadata.rowConfidence / P2.2), it only exists for
				// PDF-sourced Smart Import rows.
				const aggregatedRows = unmatchedNames.map((name) => ({
					rowId: uuid(),
					csvName: name,
					values: {},
					confidence: null as 'high' | 'low' | null,
				}));
				// computeEmployeeRows pre-fills addAsNewName for 'new' rows; clear it
				// so add-as-new is an explicit user choice here (not a silent default).
				setRows(
					computeEmployeeRows(aggregatedRows, rosterResult, aliasMap).map(
						(r) => ({ ...r, addAsNewName: null, reactivate: false })
					)
				);
				setLoading(false);
			} catch {
				if (!cancelled) {
					setLoadError('Could not load employees. Please try again.');
					setLoading(false);
				}
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, companyId, unmatchedNames]);

	const rosterById = useMemo(() => {
		const m = new Map<string, RosterEmployee>();
		for (const e of roster) m.set(e.id, e);
		return m;
	}, [roster]);

	const rosterOptions = useMemo(() => {
		const active = roster
			.filter((e) => e.isActiveInApp)
			.map((e) => ({ value: e.id, label: e.fullName }));
		const inactive = roster
			.filter((e) => !e.isActiveInApp)
			.map((e) => ({ value: e.id, label: `${e.fullName} (inactive)` }));
		// "+ Add as new employee" pinned first so any row can become an add-as-new.
		return [
			{ value: ADD_AS_NEW_VALUE, label: '+ Add as new employee' },
			...active,
			...inactive,
		];
	}, [roster]);

	const mappedCount = rows.filter((r) => r.matchedEmployeeId != null).length;
	const newCount = rows.filter((r) => r.addAsNewName != null).length;
	const totalCount = rows.length;
	const unresolvedCount = totalCount - mappedCount - newCount;

	// Something to save when at least one row is resolved (mapped) or marked new.
	const hasResolved = mappedCount > 0 || newCount > 0;

	const isInactive = (row: MismatchRow): boolean =>
		row.matchedEmployeeId != null &&
		rosterById.get(row.matchedEmployeeId)?.isActiveInApp === false;

	const selectValue = (row: MismatchRow): string | undefined => {
		if (row.matchedEmployeeId) return row.matchedEmployeeId;
		if (row.addAsNewName != null || pendingAddNew[row.rowId] != null) {
			return ADD_AS_NEW_VALUE;
		}
		return undefined;
	};

	const handleSelectChange = (row: MismatchRow, value: string) => {
		if (value === ADD_AS_NEW_VALUE) {
			// Open the inline name input, prefilled with any prior add-name or the
			// QBO name. addAsNewName is only set on confirm.
			setPendingAddNew((prev) => ({
				...prev,
				[row.rowId]: row.addAsNewName ?? row.csvName,
			}));
			return;
		}
		// Remap to an existing employee (deliberate pick → 'matched').
		clearPending(row.rowId);
		setRows((prev) =>
			prev.map((r) =>
				r.rowId === row.rowId
					? {
							...r,
							matchedEmployeeId: value,
							matchStatus: 'matched',
							matchScore: null,
							addAsNewName: null,
							reactivate:
								rosterById.get(value)?.isActiveInApp === false
									? r.reactivate
									: false,
					  }
					: r
			)
		);
	};

	const clearPending = (rowId: string) =>
		setPendingAddNew((prev) => {
			if (prev[rowId] == null) return prev;
			const next = { ...prev };
			delete next[rowId];
			return next;
		});

	const confirmAddNew = (rowId: string) => {
		const name = (pendingAddNew[rowId] ?? '').trim();
		if (!name) return; // a non-empty name is required
		setRows((prev) =>
			prev.map((r) =>
				r.rowId === rowId
					? {
							...r,
							addAsNewName: name,
							matchStatus: 'new',
							matchedEmployeeId: null,
							matchScore: null,
							reactivate: false,
					  }
					: r
			)
		);
		clearPending(rowId);
	};

	const handleReactivateToggle = (rowId: string) => {
		setRows((prev) =>
			prev.map((r) =>
				r.rowId === rowId ? { ...r, reactivate: !r.reactivate } : r
			)
		);
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			// Step 0 (ENG-352): create new employees. Capture each new UUID so the
			// alias step below can reference it. If any create fails we abort before
			// writing any alias (no partial state).
			const createdIdByRowId = new Map<string, string>();
			const addAsNewRows = rows.filter(
				(r) => r.addAsNewName && !r.matchedEmployeeId
			);
			for (const r of addAsNewRows) {
				const res = await postApi(CREATE_EMPLOYEE_URL, {
					companyId,
					fullName: r.addAsNewName,
					payPeriodId: payPeriodId ?? undefined,
				});
				const newId: string | undefined = res.data?.data?.employee?.id;
				if (!newId) throw new Error('create employee: missing id');
				createdIdByRowId.set(r.rowId, newId);
			}

			// Step 1: reactivate flagged existing employees. (Newly-created ones are
			// active already.) Standalone equivalent of the WS-4 commit's reactivate.
			const toReactivate = rows.filter(
				(r) => r.reactivate && r.matchedEmployeeId
			);
			for (const r of toReactivate) {
				await postApi(UPDATE_STATUS_URL, {
					employeeId: r.matchedEmployeeId,
					companyId,
					isActiveInApp: true,
				});
			}

			// Step 2: upsert qbo_time_name aliases for every resolved row — remapped
			// (matchedEmployeeId) or just-created (createdIdByRowId).
			const aliases: Array<{ csvValue: string; employeeId: string }> = [];
			for (const r of rows) {
				const employeeId = r.matchedEmployeeId ?? createdIdByRowId.get(r.rowId);
				if (employeeId) {
					aliases.push({ csvValue: r.csvName, employeeId });
				}
			}

			await postApi(ALIASES_URL, {
				companyId,
				mappingType: 'qbo_time_name',
				// WS-14 (ENG-333): mismatch-panel resolutions are qbo_time-provenance.
				source: 'qbo_time',
				aliases,
			});

			setSaving(false);
			onSaved();
			onClose();
		} catch {
			toastText('Failed to save mismatch resolutions. Please try again.', 'error');
			setSaving(false);
		}
	};

	const statusTag = (row: MismatchRow) => {
		if (row.addAsNewName != null) {
			return <Tag color="cyan">New employee</Tag>;
		}
		switch (row.matchStatus) {
			case 'matched':
				return <Tag color="green">Matched</Tag>;
			case 'saved':
				return <Tag color="blue">Saved</Tag>;
			case 'review':
				return <Tag color="gold">Review · {row.matchScore}</Tag>;
			case 'new':
				return <Tag color="orange">Unmatched</Tag>;
			default:
				return null;
		}
	};

	const columns: ColumnsType<MismatchRow> = [
		{
			title: 'Employee (QBO)',
			key: 'csvName',
			width: '35%',
			render: (_: unknown, row) => <strong>{row.csvName}</strong>,
		},
		{
			title: 'Status',
			key: 'status',
			width: '20%',
			render: (_: unknown, row) => statusTag(row),
		},
		{
			title: 'Maps to',
			key: 'mapsTo',
			render: (_: unknown, row) => {
				const editing = pendingAddNew[row.rowId] != null;
				return (
					<div className="qbo-mismatch-panel__map-cell">
						<Select
							style={{ width: '100%' }}
							value={selectValue(row)}
							showSearch
							placeholder="Select employee…"
							filterOption={(input, option) =>
								// The pinned sentinel is always visible regardless of search.
								option?.value === ADD_AS_NEW_VALUE ||
								String(option?.label ?? '')
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							options={rosterOptions}
							onChange={(value: string) => handleSelectChange(row, value)}
						/>

						{editing && (
							<div className="qbo-mismatch-panel__add-new">
								<Input
									size="small"
									autoFocus
									value={pendingAddNew[row.rowId]}
									placeholder="New employee name (Last, First)"
									onChange={(e) =>
										setPendingAddNew((prev) => ({
											...prev,
											[row.rowId]: e.target.value,
										}))
									}
									onPressEnter={() => confirmAddNew(row.rowId)}
								/>
								<Button
									size="small"
									type="primary"
									aria-label="Confirm new employee"
									style={{ background: '#1a3c34', borderColor: '#1a3c34' }}
									onClick={() => confirmAddNew(row.rowId)}
								>
									✓
								</Button>
								<Button
									size="small"
									aria-label="Cancel new employee"
									onClick={() => clearPending(row.rowId)}
								>
									✗
								</Button>
							</div>
						)}

						{!editing && row.addAsNewName != null && (
							<div className="qbo-mismatch-panel__add-new-confirmed">
								New employee: <strong>{row.addAsNewName}</strong>
								<Button
									type="link"
									size="small"
									onClick={() =>
										setPendingAddNew((prev) => ({
											...prev,
											[row.rowId]: row.addAsNewName as string,
										}))
									}
								>
									Edit
								</Button>
							</div>
						)}

						{isInactive(row) && (
							<div className="qbo-mismatch-panel__inactive">
								<span className="qbo-mismatch-panel__inactive-text">
									⚠ This employee is currently inactive.
								</span>
								<Button
									size="small"
									type={row.reactivate ? 'primary' : 'default'}
									style={
										row.reactivate
											? { background: '#1a3c34', borderColor: '#1a3c34' }
											: { borderColor: '#faad14', color: '#faad14' }
									}
									onClick={() => handleReactivateToggle(row.rowId)}
								>
									{row.reactivate
										? '✓ Reactivate & import'
										: 'Reactivate & import'}
								</Button>
							</div>
						)}
					</div>
				);
			},
		},
	];

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title="Resolve QBO Employee Name Mismatches"
			width={900}
			footer={null}
			destroyOnHidden
			maskClosable={false}
		>
			<div className="qbo-mismatch-panel">
				{loadError ? (
					<Alert
						type="error"
						showIcon
						message="Could not load"
						description={loadError}
					/>
				) : (
					<>
						<div className="qbo-mismatch-panel__summary">
							{totalCount} unmatched name{totalCount === 1 ? '' : 's'}:&nbsp;
							<span style={{ color: '#52c41a', fontWeight: 600 }}>
								{mappedCount} mapped
							</span>
							{newCount > 0 && (
								<>
									{' · '}
									<span style={{ color: '#13c2c2', fontWeight: 600 }}>
										{newCount} new
									</span>
								</>
							)}
							{' · '}
							<span style={{ fontWeight: 600 }}>
								{unresolvedCount} unresolved
							</span>
						</div>
						<div className="qbo-mismatch-panel__summary-subtext">
							Map each QBO name to an existing employee, or add it as a new
							one. Saved mappings auto-apply on the next sync.
						</div>

						<Spin spinning={loading}>
							<Table<MismatchRow>
								columns={columns}
								dataSource={rows}
								rowKey="rowId"
								pagination={false}
								size="small"
								scroll={{ y: 420 }}
							/>
						</Spin>
					</>
				)}

				<div className="qbo-mismatch-panel__footer">
					<Space>
						<Button onClick={onClose}>Cancel</Button>
						<Button
							type="primary"
							loading={saving}
							disabled={!!loadError || loading || !hasResolved}
							onClick={() => void handleSave()}
							style={{ background: '#1a3c34', borderColor: '#1a3c34' }}
						>
							Save
						</Button>
					</Space>
				</div>
			</div>
		</Modal>
	);
}
